import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button, Modal, Form, Table, Alert, Spinner, Container, Badge } from 'react-bootstrap';
import { FaStar, FaEdit, FaTrash, FaEye } from 'react-icons/fa';
// Browser-Build von EJS (eigenstaendiges UMD-Bundle, vermeidet Node-'fs'-Abhaengigkeit im CRA-Build)
import ejs from '../vendor/ejs.min.js';
import UserService from '../services/user.service';
import { invoicePreviewSample, formatCurrency } from '../data/invoicePreviewSample';

// Auswaehlbare Stufen fuer die Sichtbarkeit von System-Belegarten
// (Codes muessen mit der Backend-Whitelist in tierVisibility.js uebereinstimmen).
const TIER_OPTIONS = [
    { code: 'mainvo', label: 'Haupt-VO (MainVo)' },
    { code: 'subvo', label: 'Reseller (SubVo)' },
    { code: 'adm', label: 'Vertriebspartner (ADM)' },
    { code: 'customer', label: 'Kunde' },
];
const EMPTY_FORM = { name: '', description: '', template: '', is_default: false, visible_tiers: [] };

const BoardDocumentTypes = ({ scope = 'stage' }) => {
    // System-Modus: Admin verwaltet die wiederverwendbaren System-Vorlagen.
    const isSystem = scope === 'system';
    const [documentTypes, setDocumentTypes] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [currentDoc, setCurrentDoc] = useState(null);
    const [formData, setFormData] = useState({ ...EMPTY_FORM });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [isEditMode, setIsEditMode] = useState(false);
    // Lese-Modus: System-Vorlagen sind in den Stufen nur lesbar (Augen-Icon).
    const [isViewMode, setIsViewMode] = useState(false);
    // Live-Vorschau (Feature-Flag: standardmaessig aktiv, ueber Schalter ein-/ausblendbar)
    const [showPreview, setShowPreview] = useState(true);
    const [previewHtml, setPreviewHtml] = useState('');
    const [previewError, setPreviewError] = useState('');
    // Verschiebbarer Trenner: Breite der Editor-Spalte in Prozent (Desktop).
    const [editorWidth, setEditorWidth] = useState(45);
    const [isDragging, setIsDragging] = useState(false);
    const splitRef = useRef(null);
    const draggingRef = useRef(false);

    const startDrag = useCallback((e) => {
        e.preventDefault();
        draggingRef.current = true;
        setIsDragging(true);
    }, []);

    // Drag-Logik fuer den Trenner (Maus + Touch). Iframe waehrend des Ziehens deaktiviert,
    // damit der Mauszeiger nicht im iframe "haengen bleibt".
    useEffect(() => {
        const getClientX = (e) => (e.touches && e.touches.length ? e.touches[0].clientX : e.clientX);
        const onMove = (e) => {
            if (!draggingRef.current || !splitRef.current) return;
            const rect = splitRef.current.getBoundingClientRect();
            if (!rect.width) return;
            let pct = ((getClientX(e) - rect.left) / rect.width) * 100;
            pct = Math.max(20, Math.min(80, pct));
            setEditorWidth(pct);
        };
        const onUp = () => {
            if (!draggingRef.current) return;
            draggingRef.current = false;
            setIsDragging(false);
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        window.addEventListener('touchmove', onMove, { passive: false });
        window.addEventListener('touchend', onUp);
        return () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
            window.removeEventListener('touchmove', onMove);
            window.removeEventListener('touchend', onUp);
        };
    }, []);

    // Rendert das EJS-Template clientseitig mit den Muster-Daten (wie der echte Beleg).
    const renderPreview = useCallback((template) => {
        if (!template || !template.trim()) {
            setPreviewHtml('');
            setPreviewError('');
            return;
        }
        try {
            const html = ejs.render(template, { ...invoicePreviewSample, formatCurrency });
            setPreviewHtml(html);
            setPreviewError('');
        } catch (err) {
            // Syntax-/Laufzeitfehler im Template abfangen, Vorschau bleibt stabil.
            setPreviewError((err && err.message) ? err.message : String(err));
        }
    }, []);

    // Vorschau verzoegert (debounced) beim Tippen aktualisieren.
    useEffect(() => {
        if (!showModal || !showPreview) return undefined;
        const timer = setTimeout(() => renderPreview(formData.template), 400);
        return () => clearTimeout(timer);
    }, [formData.template, showPreview, showModal, renderPreview]);

    const loadDocumentTypes = useCallback(() => {
        setLoading(true);
        setError('');
        UserService.getDocumentTypes(isSystem ? 'system' : undefined)
            .then(response => {
                setDocumentTypes(response.data);
                setLoading(false);
            })
            .catch(err => {
                const errMsg = (err.response && err.response.data && err.response.data.message) || err.message || 'Unbekannter Fehler.';
                setError('Fehler beim Laden der Belegarten: ' + errMsg);
                setLoading(false);
            });
    }, [isSystem]);

    useEffect(() => {
        loadDocumentTypes();
    }, [loadDocumentTypes]);

    const handleClose = () => {
        setShowModal(false);
        setCurrentDoc(null);
        setFormData({ ...EMPTY_FORM });
        setIsEditMode(false);
        setIsViewMode(false);
        setError('');
    };

    const handleShowCreate = () => {
        setIsEditMode(false);
        setFormData({ ...EMPTY_FORM });
        setShowModal(true);
    };

    const handleShowEdit = (doc) => {
        setIsEditMode(true);
        setCurrentDoc(doc);
        setFormData({
            name: doc.name,
            description: doc.description,
            template: doc.template,
            is_default: doc.is_default,
            visible_tiers: Array.isArray(doc.visible_tiers) ? doc.visible_tiers : []
        });
        setShowModal(true);
    };

    // Oeffnet das Vollbild-Modal schreibgeschuetzt, um eine System-Vorlage
    // anzusehen/zu lesen (inkl. gerenderter Live-Vorschau).
    const handleShowView = (doc) => {
        setIsEditMode(false);
        setIsViewMode(true);
        setCurrentDoc(doc);
        setFormData({
            name: doc.name,
            description: doc.description,
            template: doc.template,
            is_default: doc.is_default,
            visible_tiers: Array.isArray(doc.visible_tiers) ? doc.visible_tiers : []
        });
        setShowModal(true);
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ 
            ...prev, 
            [name]: type === 'checkbox' ? checked : value 
        }));
    };

    const toggleTier = (code) => {
        setFormData(prev => {
            const cur = Array.isArray(prev.visible_tiers) ? prev.visible_tiers : [];
            return { ...prev, visible_tiers: cur.includes(code) ? cur.filter(c => c !== code) : [...cur, code] };
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        // Im Lese-Modus wird nichts gespeichert (Schutz vor versehentlichem Submit).
        if (isViewMode) return;
        setLoading(true);
        setError('');

        // Im System-Modus markiert owner_level='system' den Datensatz als System-Vorlage.
        const payload = isSystem ? { ...formData, owner_level: 'system' } : formData;
        const apiCall = isEditMode
            ? UserService.updateDocumentType(currentDoc.doc_type_id, payload, isSystem ? 'system' : undefined)
            : UserService.createDocumentType(payload);

        apiCall
            .then(() => {
                handleClose();
                loadDocumentTypes(); // Liste neu laden
            })
            .catch(err => {
                const errMsg = (err.response && err.response.data && err.response.data.message) || err.message || 'Unbekannter Fehler.';
                setError('Fehler beim Speichern der Belegart: ' + errMsg);
            })
            .finally(() => {
                setLoading(false);
            });
    };
    
    const handleDelete = (id) => {
        if (window.confirm('Sind Sie sicher, dass Sie diese Belegart löschen möchten?')) {
            setLoading(true);
            setError('');
            UserService.deleteDocumentType(id, isSystem ? 'system' : undefined)
                .then(() => {
                    loadDocumentTypes(); // Liste neu laden
                })
                .catch(err => {
                    const errMsg = (err.response && err.response.data && err.response.data.message) || err.message || 'Unbekannter Fehler.';
                    setError('Fehler beim Löschen der Belegart: ' + errMsg);
                    setLoading(false);
                });
        }
    };

    return (
        <Container style={{ paddingTop: '20px' }}>
            <style jsx="true">{`
                .btn-apple {
                  background-color: #007AFF;
                  border-color: #007AFF;
                  color: white;
                  border-radius: 8px;
                  padding: 10px 20px;
                  font-weight: 500;
                  transition: all 0.2s ease;
                }
                .btn-apple:hover {
                  background-color: #0056b3;
                  border-color: #0056b3;
                }
                .template-textarea {
                  font-family: 'Courier New', Courier, monospace;
                  font-size: 14px;
                  line-height: 1.5;
                  background-color: #f8f9fa;
                  flex: 1 1 auto;
                  min-height: 220px;
                  resize: none;
                }
                /* --- Vollbild-Modal (unabhaengig von Bootstrap 4/5) --- */
                .doc-fullscreen-dialog {
                  max-width: 100%;
                  width: 100%;
                  height: 100%;
                  margin: 0;
                }
                .doc-fullscreen-content {
                  height: 100vh;
                  border: 0;
                  border-radius: 0;
                  display: flex;
                  flex-direction: column;
                }
                .doc-form {
                  display: flex;
                  flex-direction: column;
                  flex: 1 1 auto;
                  min-height: 0;
                }
                .doc-modal-body {
                  flex: 1 1 auto;
                  min-height: 0;
                  overflow: hidden;
                  padding: 16px;
                }
                /* Split-Container */
                .doc-split {
                  display: flex;
                  flex-direction: row;
                  width: 100%;
                  height: 100%;
                  min-height: 0;
                }
                .doc-split.is-dragging { cursor: col-resize; user-select: none; }
                .doc-split.is-dragging iframe { pointer-events: none; }
                .doc-editor-pane {
                  display: flex;
                  flex-direction: column;
                  min-width: 0;
                  min-height: 0;
                  overflow-y: auto;
                  padding-right: 4px;
                }
                .doc-splitter {
                  flex: 0 0 10px;
                  align-self: stretch;
                  cursor: col-resize;
                  background: #e9ecef;
                  border-radius: 5px;
                  margin: 0 6px;
                  position: relative;
                }
                .doc-splitter::after {
                  content: '';
                  position: absolute;
                  top: 50%; left: 50%;
                  transform: translate(-50%, -50%);
                  width: 2px; height: 36px;
                  background: #adb5bd;
                  border-radius: 2px;
                }
                .doc-splitter:hover { background: #ced4da; }
                .doc-preview-pane {
                  display: flex;
                  flex-direction: column;
                  flex: 1 1 auto;
                  min-width: 0;
                  min-height: 0;
                }
                .preview-toolbar {
                  display: flex;
                  justify-content: space-between;
                  align-items: center;
                  margin-bottom: 8px;
                }
                .preview-frame {
                  flex: 1 1 auto;
                  width: 100%;
                  height: 100%;
                  min-height: 0;
                  border: 1px solid #dee2e6;
                  border-radius: 8px;
                  background-color: #ffffff;
                }
                .preview-empty {
                  flex: 1 1 auto;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  border: 1px dashed #ced4da;
                  border-radius: 8px;
                  color: #6c757d;
                  background-color: #f8f9fa;
                  text-align: center;
                  padding: 20px;
                }
                /* Responsiv: unter 992px gestapelt, kein Trenner, vertikales Scrollen */
                @media (max-width: 991px) {
                  .doc-split { flex-direction: column; overflow-y: auto; height: auto; }
                  .doc-editor-pane { width: 100% !important; overflow: visible; }
                  .doc-splitter { display: none; }
                  .doc-preview-pane { width: 100% !important; min-height: 60vh; margin-top: 12px; }
                  .template-textarea { min-height: 260px; }
                  .preview-frame, .preview-empty { min-height: 60vh; }
                }
            `}</style>

            <div className="d-flex justify-content-between align-items-center mb-4">
                <h1>{isSystem ? 'System-Belegarten (Vorlagen) verwalten' : 'Belegarten verwalten'}</h1>
                <Button className="btn-apple" onClick={handleShowCreate}>
                    {isSystem ? 'Neue System-Belegart erstellen' : 'Neue Belegart erstellen'}
                </Button>
            </div>

            {isSystem && (
                <Alert variant="info">
                    System-Belegarten sind wiederverwendbare Vorlagen, die allen Stufen als Standard zur Verfügung stehen.
                    Stufen können diese übernehmen oder durch eine eigene Belegart ersetzen.
                </Alert>
            )}

            {error && <Alert variant="danger">{error}</Alert>}
            
            {loading && !documentTypes.length ? (
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-2">Lade Belegarten...</p>
                </div>
            ) : (
                <Table striped bordered hover responsive>

                    <thead>
                        <tr>
                            <th style={{width: '80px'}} className="text-center">Standard</th>
                            <th>Name</th>
                            <th>Beschreibung</th>
                            {isSystem && <th style={{width: '180px'}}>Sichtbar in</th>}
                            <th style={{width: '200px'}}>Aktionen</th>
                        </tr>
                    </thead>

                    <tbody>
                        {documentTypes.map(doc => {
                            // System-Vorlagen sind in den Stufen sichtbar, aber nur lesbar
                            // (Verwaltung ausschliesslich im System-Bereich durch Admins).
                            const isSystemRow = !isSystem && doc.is_system === true;
                            return (
                            <tr key={doc.doc_type_id}>
                                <td className="text-center align-middle">
                                    {doc.is_default && <FaStar color="#ffc107" title="Standard-Belegart" size={20} />}
                                </td>
                                <td>
                                    {doc.name}
                                    {isSystemRow && <Badge bg="secondary" className="ms-2">System</Badge>}
                                </td>
                                <td>{doc.description}</td>
                                {isSystem && (
                                    <td>
                                        {Array.isArray(doc.visible_tiers) && doc.visible_tiers.length
                                            ? doc.visible_tiers.map((t) => {
                                                const opt = TIER_OPTIONS.find(o => o.code === t);
                                                return <Badge bg="info" text="dark" className="me-1" key={t}>{opt ? opt.label.replace(/\s*\(.*\)/, '') : t}</Badge>;
                                              })
                                            : <span className="text-muted small">keine</span>}
                                    </td>
                                )}
                                <td>
                                    {isSystemRow ? (
                                        <>
                                            <Button variant="outline-secondary" size="sm" onClick={() => handleShowView(doc)} className="me-2" title="Ansehen / Lesen" aria-label="Ansehen / Lesen"><FaEye /></Button>
                                            <span className="text-muted small">nur lesbar</span>
                                        </>
                                    ) : (
                                        <>
                                            <Button variant="outline-primary" size="sm" onClick={() => handleShowEdit(doc)} className="me-2" title="Bearbeiten" aria-label="Bearbeiten"><FaEdit /></Button>
                                            <Button variant="outline-danger" size="sm" onClick={() => handleDelete(doc.doc_type_id)} title="Löschen" aria-label="Löschen"><FaTrash /></Button>
                                        </>
                                    )}
                                </td>
                            </tr>
                            );
                        })}
                    </tbody>


                </Table>
            )}

            <Modal
                show={showModal}
                onHide={handleClose}
                backdrop="static"
                dialogClassName="doc-fullscreen-dialog"
                contentClassName="doc-fullscreen-content"
            >
                <Modal.Header closeButton>
                    <Modal.Title>{isViewMode ? 'Belegart ansehen (nur lesbar)' : (isEditMode ? 'Belegart bearbeiten' : 'Neue Belegart erstellen')}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmit} className="doc-form">
                    <Modal.Body className="doc-modal-body">
                        <div className={`doc-split${isDragging ? ' is-dragging' : ''}`} ref={splitRef}>
                            {/* Linke Spalte: Editor / Formular */}
                            <div
                                className="doc-editor-pane"
                                style={{ width: showPreview ? `${editorWidth}%` : '100%', flex: showPreview ? '0 0 auto' : '1 1 auto' }}
                            >
                                <Form.Group className="mb-3" controlId="formDocName">
                                    <Form.Label>Belegbezeichnung</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        placeholder="z.B. Standardrechnung"
                                        readOnly={isViewMode}
                                        required
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3" controlId="formDocDescription">
                                    <Form.Label>Beschreibung</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={2}
                                        name="description"
                                        value={formData.description}
                                        onChange={handleChange}
                                        placeholder="z.B. Für monatliche Abrechnungen an Reseller"
                                        readOnly={isViewMode}
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3 d-flex flex-column" style={{ flex: '1 1 auto', minHeight: 0 }} controlId="formDocTemplate">
                                    <Form.Label>Belegtemplate (EJS-Code)</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        name="template"
                                        className="template-textarea"
                                        value={formData.template}
                                        onChange={handleChange}
                                        placeholder="Fügen Sie hier den EJS-Code für die Vorlage ein..."
                                        readOnly={isViewMode}
                                        required
                                    />
                                    <Form.Text className="text-muted">
                                      Tipp: Sie können den Inhalt aus der Datei `invoice-template.ejs` als Startpunkt kopieren.
                                    </Form.Text>
                                </Form.Group>

                                <Form.Group className="mb-3" controlId="formDocIsDefault">
                                    <Form.Check
                                        type="checkbox"
                                        name="is_default"
                                        label="Diese Belegart als Standard festlegen"
                                        checked={!!formData.is_default}
                                        onChange={handleChange}
                                        disabled={isViewMode}
                                    />
                                    <Form.Text className="text-muted">
                                        Wenn Sie dies aktivieren, wird diese Belegart in Auswahlfeldern vorausgewählt.
                                    </Form.Text>
                                </Form.Group>

                                {isSystem && (
                                    <Form.Group className="mb-3" controlId="formDocVisibleTiers">
                                        <Form.Label>Sichtbar in Stufe(n)</Form.Label>
                                        <div className="d-flex flex-wrap" style={{ gap: '8px 24px' }}>
                                            {TIER_OPTIONS.map(opt => (
                                                <Form.Check
                                                    key={opt.code}
                                                    type="checkbox"
                                                    id={`doc-tier-${opt.code}`}
                                                    label={opt.label}
                                                    checked={(formData.visible_tiers || []).includes(opt.code)}
                                                    onChange={() => toggleTier(opt.code)}
                                                />
                                            ))}
                                        </div>
                                        <Form.Text className="text-muted">
                                            Diese System-Belegart wird nur in den ausgewählten Stufen als wiederverwendbarer Default angeboten.
                                        </Form.Text>
                                    </Form.Group>
                                )}

                                {error && <Alert variant="danger" className="mt-3">{error}</Alert>}
                            </div>

                            {/* Verschiebbarer Trenner (nur Desktop, mit Vorschau) */}
                            {showPreview && (
                                <div
                                    className="doc-splitter"
                                    role="separator"
                                    aria-orientation="vertical"
                                    aria-label="Spaltenbreite anpassen"
                                    onMouseDown={startDrag}
                                    onTouchStart={startDrag}
                                />
                            )}

                            {/* Rechte Spalte: Live-Vorschau */}
                            {showPreview && (
                                <div className="doc-preview-pane">
                                    <div className="preview-toolbar">
                                        <strong>Live-Vorschau (Musterdaten)</strong>
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            type="button"
                                            onClick={() => setShowPreview(false)}
                                        >
                                            Vorschau ausblenden
                                        </Button>
                                    </div>
                                    {previewError ? (
                                        <Alert variant="warning" className="mb-0">
                                            <strong>Template-Fehler:</strong> {previewError}
                                        </Alert>
                                    ) : previewHtml ? (
                                        <iframe
                                            title="Rechnungsvorschau"
                                            className="preview-frame"
                                            sandbox=""
                                            srcDoc={previewHtml}
                                        />
                                    ) : (
                                        <div className="preview-empty">
                                            Fügen Sie links ein EJS-Template ein, um die Vorschau zu sehen.
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        {!showPreview && (
                            <Button variant="outline-secondary" type="button" className="me-auto" onClick={() => setShowPreview(true)}>
                                Vorschau anzeigen
                            </Button>
                        )}
                        <Button variant="secondary" onClick={handleClose}>
                            {isViewMode ? 'Schließen' : 'Abbrechen'}
                        </Button>
                        {!isViewMode && (
                            <Button className="btn-apple" type="submit" disabled={loading}>
                                {loading ? <><Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> Speichern...</> : 'Speichern'}
                            </Button>
                        )}
                    </Modal.Footer>
                </Form>
            </Modal>
        </Container>
    );
};

export default BoardDocumentTypes;